import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import './ChatAnnotatorIntegration.css';

/**
 * ChatAnnotator Integration Component
 * 
 * This component provides GPT-powered functionality across the app.
 * It can be integrated with ChatAnnotator.com or OpenAI API.
 * 
 * For ChatAnnotator.com integration, you'll need to:
 * 1. Sign up at https://chatannotator.com/
 * 2. Get your API key
 * 3. Configure the API endpoint in your backend
 * 4. Update the API_BASE_URL to point to ChatAnnotator's API
 */
const ChatAnnotatorIntegration = ({ 
  onResponse, 
  context = '', 
  placeholder = 'Ask me anything...',
  showSuggestions = true 
}) => {
  const [query, setQuery] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);


  // Example queries based on context
  const getContextualSuggestions = () => {
    if (context.includes('report')) {
      return [
        "Show me revenue trends for the last 6 months",
        "Which properties have the lowest occupancy rates?",
        "Generate a report on tenant payment performance",
        "Compare property performance across all locations"
      ];
    } else if (context.includes('tenant')) {
      return [
        "Find tenants with overdue payments",
        "Show me tenant satisfaction scores",
        "List tenants with expiring leases"
      ];
    } else if (context.includes('property')) {
      return [
        "Which properties need maintenance attention?",
        "Show me property revenue breakdown",
        "Compare property performance metrics"
      ];
    }
    return [
      "Show me revenue trends",
      "Which properties have low occupancy?",
      "Generate a custom report",
      "Compare property performance"
    ];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim() || isProcessing) return;

    const userQuery = query.trim();
    setQuery('');
    setError(null);
    setIsProcessing(true);

    // Add user message to conversation
    const userMessage = { role: 'user', content: userQuery };
    setConversationHistory(prev => [...prev, userMessage]);

    try {
      // Call backend endpoint that will integrate with ChatAnnotator/OpenAI
      const response = await fetch(`${API_BASE_URL}/api/reports/custom-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: userQuery,
          context: context,
          history: conversationHistory.slice(-5) // Last 5 messages for context
        })
      });

      if (!response.ok) {
        throw new Error('Failed to process query');
      }

      const data = await response.json();
      
      // For now, use placeholder response until backend integration is complete
      // When ChatAnnotator/OpenAI is integrated, data.response will contain the AI response
      const aiResponse = data.response || generatePlaceholderResponse(userQuery, context);
      
      const assistantMessage = { role: 'assistant', content: aiResponse };
      setConversationHistory(prev => [...prev, assistantMessage]);
      
      if (onResponse) {
        onResponse(aiResponse, userQuery);
      }
    } catch (err) {
      console.error('Error processing query:', err);
      setError('Failed to process query. Please try again.');
      const errorMessage = { role: 'assistant', content: 'Sorry, I encountered an error processing your query. Please try again.' };
      setConversationHistory(prev => [...prev, errorMessage]);
    } finally {
      setIsProcessing(false);
    }
  };

  const generatePlaceholderResponse = (query, ctx) => {
    // Placeholder logic - will be replaced with actual AI responses
    const lowerQuery = query.toLowerCase();
    
    if (lowerQuery.includes('revenue') || lowerQuery.includes('income')) {
      return `I understand you're asking about revenue data. Once the ChatAnnotator/OpenAI integration is complete, I'll provide detailed revenue analysis from your database and generate comprehensive reports.`;
    } else if (lowerQuery.includes('occupancy') || lowerQuery.includes('vacancy')) {
      return `Occupancy analysis is coming soon! When the AI integration is active, I'll analyze occupancy rates across all properties and provide actionable insights.`;
    } else if (lowerQuery.includes('tenant') && (lowerQuery.includes('payment') || lowerQuery.includes('performance'))) {
      return `Tenant payment performance reports will be available once the AI backend is fully integrated. I'll analyze payment history, on-time rates, and generate comprehensive reports.`;
    } else if (lowerQuery.includes('property') && (lowerQuery.includes('compare') || lowerQuery.includes('performance'))) {
      return `Property comparison reports are planned! Once implemented, I'll compare properties across multiple metrics including revenue, occupancy, maintenance costs, and tenant satisfaction.`;
    } else {
      return `I'm ready to help you generate custom reports and answer questions! Once the ChatAnnotator/OpenAI integration is complete, I'll be able to query your databases, analyze trends, and even pull relevant information from the internet. What specific data are you looking for?`;
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
  };

  const clearHistory = () => {
    setConversationHistory([]);
  };

  return (
    <div className="chatannotator-integration">
      {showSuggestions && conversationHistory.length === 0 && (
        <div className="suggestions-panel">
          <div className="suggestions-header">Try asking:</div>
          <div className="suggestions-grid">
            {getContextualSuggestions().map((suggestion, index) => (
              <button
                key={index}
                className="suggestion-chip"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {conversationHistory.length > 0 && (
        <div className="conversation-panel">
          <div className="conversation-header">
            <span>Conversation History</span>
            <button onClick={clearHistory} className="clear-button-small">
              Clear
            </button>
          </div>
          <div className="conversation-messages">
            {conversationHistory.map((msg, index) => (
              <div key={index} className={`conversation-message ${msg.role}`}>
                <div className="message-role-icon">
                  {msg.role === 'user' ? '👤' : '🤖'}
                </div>
                <div className="message-content-text">
                  {msg.content}
                </div>
              </div>
            ))}
            {isProcessing && (
              <div className="conversation-message assistant">
                <div className="message-role-icon">🤖</div>
                <div className="message-content-text">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="query-form">
        <div className="query-input-wrapper">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            className="query-input"
            disabled={isProcessing}
          />
          <button
            type="submit"
            className="query-submit-button"
            disabled={!query.trim() || isProcessing}
          >
            {isProcessing ? (
              <span className="loading-spinner">⟳</span>
            ) : (
              <span>➤</span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatAnnotatorIntegration;

